import { useState } from 'react';
import { Check, ChevronDown, CircleDashed, Link2 } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Above this many options the popover gains a cmdk search box (a doctor roster can be long; a
// clinic list rarely is).
const SEARCH_THRESHOLD = 8;

const TRIGGER_CLASS =
  'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60';

const ROW_CLASS = 'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-accent';

/**
 * "Attach to one of these, or none" picker (generalizes the queue editor's ClinicSelect).
 * `options` is `[{id, label}]`; `value` "" selects the leading `noneLabel` row. A value that is
 * not in `options` (e.g. a doctor missing from the roster) still shows as its raw id so nothing
 * silently detaches.
 */
export function EntitySelect({
  value = '',
  onChange,
  options = [],
  noneLabel = 'None',
  icon: Icon = Link2,
  disabled = false,
  placeholder,
  ariaLabel,
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
}) {
  const [open, setOpen] = useState(false);
  const current = String(value || '');
  const selected = options.find((o) => o.id === current);
  const triggerLabel = selected ? selected.label : current || placeholder || noneLabel;
  const searchable = options.length > SEARCH_THRESHOLD;

  const pick = (id) => {
    onChange?.(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={searchable}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} aria-label={ariaLabel} className={TRIGGER_CLASS}>
          <Icon className={cn('h-4 w-4 shrink-0', current ? 'text-primary' : 'text-muted-foreground')} aria-hidden />
          <span className={cn('flex-1 truncate', current ? 'text-foreground' : 'text-muted-foreground')}>
            {triggerLabel}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      {searchable ? (
        <PopoverContent align="start" className="w-80 p-0">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                <CommandItem value={`__none__ ${noneLabel}`} onSelect={() => pick('')}>
                  <Check className={cn('h-4 w-4', !current ? 'opacity-100' : 'opacity-0')} aria-hidden />
                  <CircleDashed className="h-4 w-4 text-muted-foreground" aria-hidden />
                  <span className="truncate">{noneLabel}</span>
                </CommandItem>
                {options.map((o) => (
                  <CommandItem key={o.id} value={`${o.label} ${o.id}`} onSelect={() => pick(o.id)}>
                    <Check className={cn('h-4 w-4', current === o.id ? 'opacity-100' : 'opacity-0')} aria-hidden />
                    <Icon className="h-4 w-4 text-primary" aria-hidden />
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      ) : (
        <PopoverContent align="start" className="w-72 p-1.5">
          <button
            type="button"
            onClick={() => pick('')}
            className={cn(ROW_CLASS, !current ? 'bg-accent font-semibold' : '')}
          >
            <CircleDashed className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            {noneLabel}
          </button>
          {options.length === 0 ? (
            <div className="px-2 py-1.5 text-[12px] italic text-muted-foreground">{emptyText}</div>
          ) : (
            options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => pick(o.id)}
                className={cn(ROW_CLASS, current === o.id ? 'bg-accent font-semibold' : '')}
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                <span className="truncate">{o.label}</span>
              </button>
            ))
          )}
        </PopoverContent>
      )}
    </Popover>
  );
}
